import { calcularFaturamentoPorCategoria, type CategoriasComissaoConfig } from './faturamento';
import { calcComissaoEspelho, getMetaForEspelho, getRulesForEspelho } from './calcComissaoEspelhoExcel';
import { consultoresDoSegmentoDoGestor, contaParaMetaN, ajudaNaMetaDoGestor } from './regrasContagem';
import type { CommissionRule, MetaMensal } from './regras';
import type { ConvergenciaSaldoLote } from './ConvergenciaSaldoLote';
import type { DeflatorLote } from './DeflatorCancelamentoLote';
import { aplicarNegativoNoFinal, descontoEsim, escopoEsimDoGestor, type BaseAtivacaoProdutividade } from '../qualidade/produtividade-ativacao';

/**
 * O gestor com equipes nos dois segmentos ganha um lado para cada (Douglas
 * 21/10/2026, maquete aprovada): a Cadija (gerente CNPJ) passa a ter também o
 * lado CPF da CONECTA. Cada lado tem a sua meta (a soma das metas dos
 * consultores daquele segmento), a sua faixa (só o faturamento do segmento), o
 * seu % (Regras → cargo → botão CPF/CNPJ) e paga só se bater a meta dele. O
 * total a receber é a soma. Ligado já em outubro/2026 para ver no painel
 * (Douglas 21/10/2026: "já ajuste para este mês").
 */
export const GESTOR_DOIS_SEGMENTOS_DESDE = '2026-10';

type Segmento = 'PF' | 'PJ';
type ConsultorHierarquia = {
  consultor_id: string; equipe_nome?: string | null; da_diretoria?: boolean; em_experiencia?: boolean;
  is_lideranca?: boolean; is_equipe_qualidade?: boolean; fora_da_meta?: boolean;
};

export interface LadoOutroSegmento {
  segmento: Segmento;
  comissao: number;
  fatTotal: number;
  bateuMeta: boolean;
  faixa: number;
}

/** Segmentos além do próprio que o gestor tem na equipe, na competência. */
export function outrosSegmentosDoGestor(
  mesTrabalho: string,
  segmentoDoGestor: Segmento,
  hierarquia: Array<{ equipe_nome?: string | null }>,
  segmentoPorNomeEquipe: Map<string, Segmento>,
): Segmento[] {
  if (mesTrabalho < GESTOR_DOIS_SEGMENTOS_DESDE) return [];
  const outro: Segmento = segmentoDoGestor === 'PF' ? 'PJ' : 'PF';
  return consultoresDoSegmentoDoGestor(hierarquia, outro, segmentoPorNomeEquipe).length > 0 ? [outro] : [];
}

/** Meta do lado = soma das metas dos consultores daquele segmento que contam no N. */
export function metaSomadaDosConsultores(
  consultores: ConsultorHierarquia[],
  segmento: Segmento,
  metasGen: MetaMensal[],
  metasIndiv: MetaMensal[],
  metasEquipe: MetaMensal[],
  equipeIdPorNome: Map<string, string>,
  ativoNoMes: (id: string) => boolean,
): MetaMensal | null {
  const soma: Record<string, number | boolean> = {};
  let alguma = false;
  const campos = ['altas', 'fibras', 'fat_vn', 'fat_total', 'qtd_tt', 'qtd_mp', 'qtd_migracao', 'fat_ti', 'fat_avancada', 'sd', 'tv'];
  for (const c of consultores) {
    if (!contaParaMetaN({ ...c, ativo: ativoNoMes(c.consultor_id) } as Parameters<typeof contaParaMetaN>[0])) continue;
    const equipeId = c.equipe_nome ? equipeIdPorNome.get(c.equipe_nome) ?? null : null;
    const meta = getMetaForEspelho(metasGen, metasIndiv, 'CONSULTOR', c.consultor_id, metasEquipe, equipeId, segmento) as unknown as Record<string, unknown> | null;
    if (!meta) continue;
    for (const k of campos) {
      if (meta[`usar_meta_${k}`]) {
        soma[`meta_${k}`] = Number(soma[`meta_${k}`] ?? 0) + Number(meta[`meta_${k}`] ?? 0);
        soma[`usar_meta_${k}`] = true;
        alguma = true;
      }
    }
  }
  if (!alguma) return null;
  for (const k of campos) {
    soma[`meta_${k}`] = Number(soma[`meta_${k}`] ?? 0);
    soma[`usar_meta_${k}`] = !!soma[`usar_meta_${k}`];
  }
  return { ...soma, multiplicador_n: false, ativo: true } as unknown as MetaMensal;
}

/** Calcula um lado de outro segmento do gestor, com a mesma conta do lado principal. */
export function calcularLadoOutroSegmento(p: {
  segmento: Segmento;
  gestorId: string;
  cargo: string;
  hierarquia: ConsultorHierarquia[];
  segmentoPorNomeEquipe: Map<string, Segmento>;
  itensPagos: Array<{ consultor_id: string; produto: string; quantidade: number; subtotal: number; fora_sul?: boolean | null; pontua_segmento?: boolean | null }>;
  calcCfg: CategoriasComissaoConfig;
  allRulesGen: CommissionRule[];
  allRulesIndiv: CommissionRule[];
  metasGen: MetaMensal[];
  metasIndiv: MetaMensal[];
  metasEquipe: MetaMensal[];
  equipeIdPorNome: Map<string, string>;
  ativoNoMes: (id: string) => boolean;
  matrizRenovacao: unknown[];
  convLote: Pick<ConvergenciaSaldoLote, 'saldoDe'>;
  deflatorLote: Pick<DeflatorLote, 'calcular'>;
  baseAtivacao?: BaseAtivacaoProdutividade;
}): LadoOutroSegmento {
  const consultores = consultoresDoSegmentoDoGestor(p.hierarquia, p.segmento, p.segmentoPorNomeEquipe);
  const semDiretoria = consultores.filter((c) => !c.da_diretoria && ajudaNaMetaDoGestor(c as Parameters<typeof ajudaNaMetaDoGestor>[0]));
  const idsPagos = new Set(semDiretoria.map((c) => c.consultor_id));
  const idsMeta = new Set(consultores.filter((c) => ajudaNaMetaDoGestor(c as Parameters<typeof ajudaNaMetaDoGestor>[0])).map((c) => c.consultor_id));
  const itens = p.itensPagos.filter((i) => idsPagos.has(i.consultor_id));
  const itensMeta = p.itensPagos.filter((i) => idsMeta.has(i.consultor_id) && i.pontua_segmento !== false);
  const pago = calcularFaturamentoPorCategoria(itens, p.calcCfg);
  const metaCalc = calcularFaturamentoPorCategoria(itensMeta, p.calcCfg);
  const rules = getRulesForEspelho(p.allRulesGen, p.allRulesIndiv, p.cargo, p.gestorId, p.segmento);
  const meta = metaSomadaDosConsultores(consultores, p.segmento, p.metasGen, p.metasIndiv, p.metasEquipe, p.equipeIdPorNome, p.ativoNoMes);
  const r = calcComissaoEspelho(rules, pago.faturamentos, pago.quantidades, meta, 0, metaCalc.faturamentos, metaCalc.quantidades,
    p.matrizRenovacao as never, itens as never);
  const bateuMeta = !!meta && r.bateuMeta;
  const bruta = bateuMeta ? r.comissao : 0;
  const escopo = consultores.filter((c) => !c.fora_da_meta).map((c) => c.consultor_id);
  const conv = p.convLote.saldoDe(escopo, p.cargo, bateuMeta);
  const aposConv = Math.max(0, bruta - conv.perda + conv.ganho);
  const defl = p.deflatorLote.calcular(escopo, p.cargo, aposConv);
  const antesEsim = Math.max(0, aposConv - defl.desconto);
  const esim = descontoEsim(p.baseAtivacao, { pessoaId: p.gestorId, idsEscopo: escopoEsimDoGestor(consultores), cargo: p.cargo, bateuMeta, percentuais: { pn: r.pctPn, ha: r.pctHa } });
  return { segmento: p.segmento, comissao: aplicarNegativoNoFinal(antesEsim, esim), fatTotal: pago.faturamentos.total, bateuMeta, faixa: r.faixa };
}
